import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { serviceTokenStore } from "../../utils/serviceTokenStore";
import Header from "../../containers/Header";
import Button from "../../components/shared/Button";
import Loader from "../../components/shared/Loader";
import CompletedBadge from "../../components/shared/CompletedBadge";
import BubblingAIAssistant from "../../components/BubblingAIAssistant";
import { useAppContext } from "../../context/AppContext";
import { useLocalization } from "../../context/LocalizationContext";
import { convertToArabicNumerals } from "../../utils/helpers";
import "./styles.css";

interface AssessmentProps {
  userData?: unknown;
}

const Assessment: React.FC<AssessmentProps> = ({ userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setCurrentCategory,
    categories,
    savedAnswers,
    loadCategories,
    isLoadingCategories,
    categoriesError,
    completedCategories,
  } = useAppContext();
  const { t, isRTL } = useLocalization();
  const userDataFromState = location.state?.userData || userData;
  const autoStartAssessment = location.state?.autoStartAssessment;

  useEffect(() => {
    if (categories.length === 0 && !isLoadingCategories && serviceTokenStore.getToken()) {
      loadCategories();
    }
  }, [categories.length, isLoadingCategories, loadCategories]);

  const handleAssessmentClick = (assessment: { id: number; name: string }) => {
    setCurrentCategory(assessment.id);
    navigate(`/assessment/${assessment.id}`, {
      state: {
        assessment,
        userData: userDataFromState,
      },
    });
  };

  useEffect(() => {
    if (autoStartAssessment && categories.length > 0) {
      const nextAssessment = categories.find(
        (cat: { name: string }) => cat.name === autoStartAssessment
      );
      if (nextAssessment) {
        handleAssessmentClick(nextAssessment);
      }
    }
  }, [autoStartAssessment, categories]);

  if (isLoadingCategories) {
    return (
      <div className="page-container scrollable-page">
        <Loader text={t("loadingAssessments")} />
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="page-container scrollable-page">
        <div className="error-message">{categoriesError}</div>
      </div>
    );
  }

  const hasAnyStarted = Object.values(savedAnswers).some(
    (answers) => answers && Object.keys(answers).length > 0
  );

  const nextCategory = categories.find(
    (cat: { id: number }) => !completedCategories.includes(cat.id)
  );

  return (
    <div className="page-container scrollable-page">
      <div className="assessment-content">
        <Header
          title={t("assessment.title")}
          statusText={`${t("loggedInAs")} ${(userDataFromState as { name?: string })?.name || (userData as { name?: string })?.name || ""}`.trim()}
          showHomeButton
          showSettingsButton
        />
        <div className="assessments-list">
          {categories.map((assessment: { id: number; name: string; title: string; questionCount: number }) => {
            const categoryAnswers = savedAnswers[assessment.id];
            const answeredCount = categoryAnswers ? Object.keys(categoryAnswers).length : 0;
            const isCompleted = completedCategories.includes(assessment.id);
            return (
              <Button
                key={assessment.id}
                variant="card"
                className={`assessment-item assessment-section`}
                onClick={() => handleAssessmentClick(assessment)}
              >
                <div className="assessment-info">{assessment.title}</div>
                {isCompleted ? <CompletedBadge />:
                <div className="assessment-question-count">
                  {isRTL ? (
                    `${convertToArabicNumerals(answeredCount)}/${convertToArabicNumerals(assessment.questionCount)}`
                  ) : (
                    `${answeredCount}/${assessment.questionCount}`
                  )}
                </div>}
              </Button>
            );
          })}
        </div>

        {(!hasAnyStarted && categories.length > 0) ? (
          <div className="assessment-page-footer">
            <Button
              variant="solid"
              size="lg"
              className="get-started-btn"
              onClick={() => handleAssessmentClick(categories[0])}
            >
              {t("assessment.getStarted")}
            </Button>
          </div>
        ) : (hasAnyStarted && nextCategory) ? (
          <div className="assessment-page-footer next-section-footer">
            <div className="next-info">
              <span className="next-label">{t("next")}</span>
              <h3 className="next-title">{nextCategory.title}</h3>
            </div>
            <Button
              variant="secondary"
              size="lg"
              className="continue-btn"
              onClick={() => handleAssessmentClick(nextCategory)}
            >
              {t("continue")}
            </Button>
          </div>
        ) : null}
      </div>
      <BubblingAIAssistant />
    </div>
  );
};

export default Assessment;
